// web/src/lib/authDev.js
// Dev auth stored in localStorage (+ keep Firebase Auth in sync)
// This API matches your existing SignIn.jsx:
//   signIn(email, password)
//   getSession()
//   signOut()

import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signOut as fbSignOut,
} from 'firebase/auth'

const SESSION_KEY = 'session'
const DEV_PASSWORD = 'password' // keep your existing UX hint

/* ---------------- util ---------------- */
const hasWindow = () =>
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'

function dispatchSessionChange() {
  if (!hasWindow()) return
  window.dispatchEvent(new Event('sessionchange'))
}

/* ------------- firebase helpers ------------- */
async function ensureFirebaseSignedIn() {
  try {
    const auth = getAuth()
    if (auth.currentUser) return auth.currentUser

    // Wait one tick in case auth state is already resolving
    await new Promise((resolve) => {
      let done = false
      const unsub = onAuthStateChanged(auth, () => {
        if (!done) {
          done = true
          unsub?.()
          resolve()
        }
      })
      setTimeout(() => {
        if (!done) {
          done = true
          unsub?.()
          resolve()
        }
      }, 0)
    })

    if (!auth.currentUser) {
      await signInAnonymously(auth).catch(() => {
        // Best effort: if anonymous sign-in fails, UI still works;
        // Firestore reads may be blocked by rules until enabled.
      })
    }
    return getAuth().currentUser
  } catch {
    return null
  }
}

async function firebaseSignOutSafe() {
  try {
    const auth = getAuth()
    if (auth?.currentUser) await fbSignOut(auth)
  } catch {
    // ignore
  }
}

/* ---------------- public API ---------------- */
export function getSession() {
  if (!hasWindow()) return null
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
  } catch {
    return null
  }
}

/**
 * signIn(email, password)
 * - Validates minimal dev rules
 * - Stores session in localStorage for UI
 * - Ensures Firebase Auth user (anonymous) exists for Firestore rules
 */
export function signIn(email, password) {
  if (!hasWindow()) throw new Error('Not in a browser environment')
  const em = (email || '').trim()
  if (!em) throw new Error('Email is required.')
  // Optional: enforce the dev password (matches your UI note)
  if (password !== DEV_PASSWORD) throw new Error('Invalid password.')

  const now = new Date().toISOString()
  const displayName = em.split('@')[0].replace(/\W+/g, ' ') || 'User'

  const session = {
    email: em,
    displayName,
    title: 'Manager', // default; your UI or sysconfig can adjust later
    adminLevel: '',
    createdAt: now,
    lastLoginAt: now,
  }

  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  dispatchSessionChange()

  // Kick off Firebase anonymous sign-in but don't block UI
  // If you prefer to block until done, `await ensureFirebaseSignedIn()`
  // (keeping it async but fire-and-forget is snappier for the UI).
  ensureFirebaseSignedIn()

  return session
}

export async function signOut() {
  if (hasWindow()) {
    try {
      localStorage.removeItem(SESSION_KEY)
    } catch { /* ignore */ }
    dispatchSessionChange()
  }
  await firebaseSignOutSafe()
}

/** Convenience subscription for session changes */
export function onSessionChange(handler) {
  if (!hasWindow() || typeof handler !== 'function') return () => {}
  const fn = () => handler(getSession())
  window.addEventListener('sessionchange', fn)
  return () => window.removeEventListener('sessionchange', fn)
}